import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "../lib/api";
import { formatDate } from "../lib/format";
import DeleteConfirmModal from "./DeleteConfirmModal";

const STATUSES = ["new","contacted","confirmed","completed","cancelled","no_show"];

const AdminTestDrives = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, label }

  const load = async () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    const r = await api.get(`/admin/test-drives?${params.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (id, patch) => {
    try { const r = await api.patch(`/admin/test-drives/${id}`, patch); setItems(items.map((x) => x.id === id ? r.data : x)); toast.success("Updated"); }
    catch { toast.error("Failed"); }
  };

  const requestDelete = (it) => setPendingDelete({ id: it.id, label: `${it.name} — ${it.vehicle_summary || "test drive"}` });
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/test-drives/${pendingDelete.id}`);
      toast.success("Test drive request deleted");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-test-drives">
      <div>
        <div className="overline text-[#7A1519]">Test Drives</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Test Drive Requests</h1>
      </div>
      <div className="flex gap-1 flex-wrap">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-sm border hairline ${filter === "" ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-sm border hairline capitalize ${filter === s ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>

      <div className="bg-white border hairline rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F3] border-b hairline text-left text-[#242424]/60">
            <tr>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Vehicle</th>
              <th className="px-4 py-3 font-medium">Preferred</th>
              <th className="px-4 py-3 font-medium">Submitted</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#242424]/60">No requests.</td></tr>}
            {items.map((it) => (
              <React.Fragment key={it.id}>
                <tr className="border-b hairline hover:bg-[#F7F6F3]/50 cursor-pointer" onClick={() => setExpanded(expanded === it.id ? null : it.id)} data-testid={`td-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-[#242424]/60">{it.phone} · {it.email}</div>
                  </td>
                  <td className="px-4 py-3">{it.vehicle_summary || "—"}</td>
                  <td className="px-4 py-3">{it.preferred_date} {it.preferred_time}</td>
                  <td className="px-4 py-3 text-xs text-[#242424]/60">{formatDate(it.created_at)}</td>
                  <td className="px-4 py-3">
                    <select value={it.status} onChange={(e) => { e.stopPropagation(); update(it.id, { status: e.target.value }); }} onClick={(e) => e.stopPropagation()} className="bg-white border hairline px-2 py-1 rounded-sm text-xs capitalize" data-testid={`td-status-${it.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); requestDelete(it); }} className="p-2 border hairline rounded-sm hover:bg-[#F7F6F3] text-[#7A1519]" title="Delete" data-testid={`td-del-${it.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
                {expanded === it.id && (
                  <tr className="bg-[#F7F6F3]/40">
                    <td colSpan={6} className="p-4">
                      {it.message && <div className="mb-3 text-sm"><span className="overline text-[#242424]/60 mr-2">Message:</span>{it.message}</div>}
                      <label className="overline text-[#242424]/60 block mb-1">Internal notes</label>
                      <textarea rows={3} defaultValue={it.internal_notes} onBlur={(e) => e.target.value !== it.internal_notes && update(it.id, { internal_notes: e.target.value })} className="w-full bg-white border hairline rounded-sm p-3 text-sm" data-testid={`td-notes-${it.id}`} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteConfirmModal
        open={!!pendingDelete}
        recordType="test drive request"
        recordLabel={pendingDelete?.label || ""}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminTestDrives;
